import React, { Suspense } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2 } from 'lucide-react';
import { dashboardRoutes } from './routes';
import LandingPage from '../features/landing/components/LandingPage';
import DashboardHome from '../features/dashboard/components/DashboardHome';
import { DynamicFooter } from '../shared/ui/DynamicFooter';
import UpgradeGate from '../shared/ui/UpgradeGate';
import PageErrorBoundary from '../shared/ui/PageErrorBoundary';
import APSubNav from '../features/accounts-payable/components/APSubNav';
import ARSubNav from '../features/accounts-receivable/components/ARSubNav';

// AP detail pages that should show the sub-navigation
const AP_DETAIL_ROUTES = new Set([
  '#ap-procurement', '#ap-bills', '#ap-vendors', '#ap-banking', '#ap-reports',
  '#purchase-requisitions', '#purchase-orders', '#goods-receiving',
  '#bill-processing', '#bill-scanner', '#payment-processing', '#scheduled-payments', '#recurring-bills',
  '#vendor-mgmt', '#vendor-performance', '#vendor-statements', '#vendor-portal',
  '#bank-feeds', '#bank-reconciliation',
  '#ap-aging', '#expense-reports', '#audit-trail', '#ap-analytics', '#ap-consolidated', '#ap-supply-chain',
]);

// AR detail pages that should show the sub-navigation
const AR_DETAIL_ROUTES = new Set([
  '#ar-customers', '#ar-invoicing', '#ar-payments', '#ar-credit', '#ar-reports',
  '#customer-mgmt', '#ar-communications', '#ar-reminders', '#ar-customer-portal',
  '#invoicing', '#ar-recurring-invoices', '#ar-templates', '#ar-estimates',
  '#ar-payment-recording', '#ar-payment-plans', '#ar-online-payments',
  '#credit-mgmt', '#ar-collections', '#ar-credit-exposure', '#ar-ai-collections', '#ar-dunning',
  '#aging-reports', '#ar-statements', '#ar-revenue-reports', '#ar-bank-recon',
  '#ar-rev-recognition', '#ar-consolidated',
]);

const ROUTE_FEATURE: Record<string, string> = {
  '#customer-mgmt': 'customers',
  '#invoicing': 'invoices',
  '#credit-mgmt': 'customers',
  '#aging-reports': 'customers',
  '#ar-customers': 'customers',
  '#ar-invoicing': 'invoices',
  '#ar-payments': 'invoices',
  '#ar-credit': 'customers',
  '#ar-reports': 'customers',
  '#ar-communications': 'customers',
  '#ar-reminders': 'customers',
  '#ar-customer-portal': 'customers',
  '#ar-recurring-invoices': 'invoices',
  '#ar-templates': 'invoices',
  '#ar-estimates': 'invoices',
  '#ar-payment-recording': 'invoices',
  '#ar-payment-plans': 'invoices',
  '#ar-online-payments': 'invoices',
  '#ar-collections': 'customers',
  '#ar-credit-exposure': 'customers',
  '#ar-ai-collections': 'customers',
  '#ar-dunning': 'customers',
  '#ar-statements': 'customers',
  '#ar-revenue-reports': 'customers',
  '#ar-bank-recon': 'customers',
  '#ar-rev-recognition': 'invoices',
  '#ar-consolidated': 'customers',
  '#ap-procurement': 'expenses',
  '#ap-bills': 'expenses',
  '#ap-vendors': 'expenses',
  '#ap-banking': 'expenses',
  '#ap-reports': 'expenses',
  '#vendor-mgmt': 'vendors',
  '#purchase-orders': 'expenses',
  '#goods-receiving': 'expenses',
  '#bill-processing': 'expenses',
  '#payment-processing': 'expenses',
  '#expense-reports': 'expenses',
  '#ap-aging': 'expenses',
  '#vendor-statements': 'expenses',
  '#recurring-bills': 'expenses',
  '#bill-scanner': 'expenses',
  '#bank-feeds': 'expenses',
  '#vendor-portal': 'expenses',
  '#purchase-requisitions': 'expenses',
  '#vendor-performance': 'expenses',
  '#scheduled-payments': 'expenses',
  '#bank-reconciliation': 'expenses',
  '#audit-trail': 'expenses',
  '#ap-analytics': 'expenses',
  '#ap-consolidated': 'expenses',
  '#ap-supply-chain': 'expenses',
  '#integrations': 'settings',
  '#tax-rules': 'settings',
  '#chart-accounts': 'accounts',
  '#journal-entries': 'journal_entries',
  '#trial-balance': 'accounts',
  '#account-recon': 'reconciliation',
  '#recurring-transactions': 'recurring',
  '#period-closing': 'period_closing',
  '#inventory-dashboard': 'inventory',
  '#products-stock': 'inventory',
  '#warehouse': 'inventory',
  '#inventory-analysis': 'inventory',
  '#asset-register': 'fixed_assets',
  '#depreciation': 'fixed_assets',
  '#asset-disposal': 'fixed_assets',
  '#asset-reports': 'fixed_assets',
  '#employee-mgmt': 'payroll',
  '#time-attendance': 'payroll',
  '#payroll-processing': 'payroll',
  '#benefits-mgmt': 'payroll',
  '#tax-compliance': 'payroll',
  '#payroll-reports': 'payroll',
  '#payroll-settings': 'payroll',
  '#ai-analytics': 'payroll',
  '#workflow-automation': 'payroll',
  '#tax-scraper-mgmt': 'payroll',
  '#smart-analysis': 'ai_assistant',
  '#automated-reports': 'ai_assistant',
  '#predictive-analytics': 'ai_assistant',
  '#ai-recommendations': 'ai_assistant',
  '#data-mgmt': 'data_management',
};

interface AppRouterProps {
  currentPath: string;
  isDashboardView: boolean;
  user: any;
  onGetStarted: () => void;
}

const PageTransition = ({ children, routeKey }: { children: React.ReactNode; routeKey: string }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
    <motion.div
      key={routeKey}
      initial={{ opacity: 0, x: isMobile ? 30 : 10, scale: isMobile ? 0.98 : 1 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: isMobile ? -30 : -10, scale: isMobile ? 0.98 : 1 }}
      transition={{ duration: isMobile ? 0.25 : 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      className="flex-1 flex flex-col overflow-hidden min-h-0 min-w-0"
    >
      {children}
    </motion.div>
  );
};

const LoadingFallback = () => (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
  </div>
);

export default function AppRouter({ currentPath, isDashboardView, user, onGetStarted }: AppRouterProps) {
  const matchedRoute = dashboardRoutes.find(r => r.path === currentPath);

  const getRouteProps = (path: string) => {
    switch (path) {
      case '#credit-mgmt':
      case '#aging-reports':
        return { onNavigate: (view: string) => window.location.hash = view };
      case '#vendor-mgmt':
        return { onBack: () => window.location.hash = '#dashboard' };
      default:
        return {};
    }
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {!isDashboardView ? (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex-1 overflow-auto custom-scrollbar min-h-0 min-w-0"
          >
            <LandingPage onGetStarted={onGetStarted} />
          </motion.div>
        ) : matchedRoute ? (
          <PageTransition routeKey={matchedRoute.path}>
            <PageErrorBoundary fallbackTitle="Page failed to load">
              <Suspense fallback={<LoadingFallback />}>
                {AP_DETAIL_ROUTES.has(matchedRoute.path) && <APSubNav />}
                {AR_DETAIL_ROUTES.has(matchedRoute.path) && <ARSubNav />}
                {ROUTE_FEATURE[matchedRoute.path] ? (
                  <UpgradeGate feature={ROUTE_FEATURE[matchedRoute.path]}>
                    <matchedRoute.component {...getRouteProps(matchedRoute.path)} />
                  </UpgradeGate>
                ) : (
                  <matchedRoute.component {...getRouteProps(matchedRoute.path)} />
                )}
              </Suspense>
            </PageErrorBoundary>
          </PageTransition>
        ) : (
          <PageTransition routeKey="dashboard-home">
            <DashboardHome user={user} />
          </PageTransition>
        )}
      </AnimatePresence>
      {currentPath !== '#landing' && currentPath !== '#login' && currentPath !== '#signup' && (
        <DynamicFooter currentPath={currentPath} />
      )}
    </>
  );
}
